<template>
  <div>
    <BModal
      :modal-name="modalName"
      width="660px"
      height="780px"
    >
      <BHeightAdjuster>
        <BModalHeader
          class="mb-400"
          :title="$t('training.callFb.aiFeedbackResult.title')"
          center
          @modal-close="handleCloseClick"
        >
          <template
            v-if="callFeedback.aiFeedbackResult && authorize(['owner'])"
            #subtitle
          >
            <ElTag
              v-if="reminderConfirmed"
              type="info"
              effect="light"
              size="medium"
              class="mt-50"
              round
              disable-transitions
            >
              {{ $t('training.callFb.aiFeedbackResult.read') }}
            </ElTag>
            <ElTag
              v-else
              type="danger"
              effect="dark"
              size="medium"
              class="mt-50"
              round
              disable-transitions
            >
              {{ $t('training.callFb.aiFeedbackResult.unread') }}
            </ElTag>
          </template>
        </BModalHeader>
        <BModalBody>
          <BLayout
            v-if="callFeedback.aiFeedbackResult"
            justify-center
            align-center
            column
          >
            <BListItem class="mb-300">
              <div class="fw-bold fs-100 mb-100">
                <BIcon
                  outlined
                  class="mr-50"
                >
                  wand_shine
                </BIcon>
                {{ $t('training.callFb.aiFeedbackResult.whyYouWhyNow') }}
              </div>
              {{ callFeedback.aiFeedbackResult.whyYouWhyNow }}
            </BListItem>
            <BListItem class="mb-300">
              <div class="fw-bold fs-100 mb-100">
                <BIcon
                  outlined
                  class="mr-50"
                >
                  wand_shine
                </BIcon>
                <template v-if="callFeedback.aiFeedbackResult.spin">
                  {{ $t('training.callFb.aiFeedbackResult.spin') }}
                </template>
                <template v-else>
                  {{ $t('training.callFb.aiFeedbackResult.nextHook') }}
                </template>
              </div>
              {{ callFeedback.aiFeedbackResult.spin }}
              {{ callFeedback.aiFeedbackResult.nextHook }}
            </BListItem>
            <BListItem
              v-if="callFeedback.aiFeedbackResult.hearing"
              class="mb-300"
            >
              <div class="fw-bold fs-100 mb-100">
                <BIcon
                  outlined
                  class="mr-50"
                >
                  wand_shine
                </BIcon>
                {{ $t('training.callFb.aiFeedbackResult.hearing') }}
              </div>
              {{ callFeedback.aiFeedbackResult.hearing }}
            </BListItem>
            <BListItem>
              {{ callFeedback.aiFeedbackResult.finally }}
            </BListItem>
          </BLayout>
          <BLayout
            v-loading="!callFeedback.aiFeedbackResult"
            :element-loading-text="$t('transcription.loading')"
            justify-center
            align-center
            fill-height
          >
          </BLayout>
        </BModalBody>
        <BModalFooter
          v-if="callFeedback.aiFeedbackResult"
        >
          <BLayout
            v-if="callFeedback.operator.id === currentUser.id"
            justify-center
            class="reminded"
          >
            <BCheckbox
              class="mb-100"
              :label="$t('training.callFb.aiFeedbackResult.reminded')"
              :disabled="reminderConfirmed"
              @click="handleConfirmReminder"
            />
          </BLayout>
          <BLayout
            v-if="isReminderableUser"
            align-center
            column
          >
            <BTooltip
              v-if="callFeedback.aiFeedbackResult.remindedAt"
              :content="reminderCancelBtnDisabledReason"
              :disabled="!reminderCancelBtnDisabledReason"
              top
            >
              <BBtn
                class="mt-200 mb-100"
                type="primary"
                outline
                :disabled="!!reminderCancelBtnDisabledReason"
                @click="handleCancelReminder"
              >
                {{ $t('training.callFb.aiFeedbackResult.cancelReminder') }}
              </BBtn>
            </BTooltip>
            <BBtn
              v-else
              class="mt-200 mb-100"
              type="primary"
              :disabled="reminderConfirmed"
              @click="handleSendReminder"
            >
              <BIcon
                filled
                type="white"
                class="mr-50"
              >
                notifications
              </BIcon>
              {{ $t('training.callFb.aiFeedbackResult.sendReminder') }}
            </BBtn>
          </BLayout>
          <BLayout
            justify-center
          >
            <CallResultDetailLink :call-result-id="callFeedback.callResultId" />
          </BLayout>
        </BModalFooter>
      </BHeightAdjuster>
    </BModal>
  </div>
</template>

<script lang="ts" setup>
import { watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { CallFeedback } from '@/api/openapi';
import { AiFeedbackResultApiService } from '@/api/user/resources/ai_feedback_result';
import { NotificationApiService } from '@/api/user/resources/notification';
import { useCurrentUserProvider } from '@/composable/current-user';
import { useWait } from '@/composable/vue-wait';
import CallResultDetailLink from '../general/CallResultDetailLink.vue';

type TProps = {
  modalName: string;
  callFeedback: CallFeedback;
};

const props = defineProps<TProps>();
const emit = defineEmits(['close', 'refetch']);
const notificationApi = new NotificationApiService();
const aiFeedbackResultApi = new AiFeedbackResultApiService();

const { doActionWithWait } = useWait();
const i18n = useI18n();
const { inject: injectCurrentUser } = useCurrentUserProvider();
const { authorize, currentUser } = injectCurrentUser();

const reminderConfirmed = computed(() => {
  return !!props.callFeedback.aiFeedbackResult?.confirmedAt;
});

const isReminderableUser = computed(() => {
  return authorize(['owner']) && props.callFeedback.operator.id !== currentUser.value.id;
});

const reminderCancelBtnDisabledReason = computed(() => {
  if (reminderConfirmed.value) return i18n.t('training.callFb.aiFeedbackResult.reminderCancelBtnDisabledReason.alreadyConfirmed');
  if (props.callFeedback.aiFeedbackResult.remindedById !== currentUser.value.id) return i18n.t('training.callFb.aiFeedbackResult.reminderCancelBtnDisabledReason.remindedByOthers');
  return null;
});

watch(() => props.callFeedback, async () => {
  if (props.callFeedback?.aiFeedbackResult) {
    await readAiFeedback();
  }
});

const readAiFeedback = async () => {
  await doActionWithWait('readAiFeedbackWait', async () => {
    await notificationApi.readAiFeedbackResult(props.callFeedback.aiFeedbackResult);
  });
};

const handleCloseClick = async () => {
  emit('close');
};

const handleSendReminder = async () => {
  await doActionWithWait('remindAiFeedbackWait', async () => {
    await aiFeedbackResultApi.remindAiFeedbackResult(props.callFeedback.aiFeedbackResult);
    emit('refetch');
  });
};

const handleCancelReminder = async () => {
  await doActionWithWait('cancelReminderAiFeedbackWait', async () => {
    await aiFeedbackResultApi.cancelReminderAiFeedbackResult(props.callFeedback.aiFeedbackResult);
    emit('refetch');
  });
};

const handleConfirmReminder = async () => {
  await doActionWithWait('confirmAiFeedbackWait', async () => {
    await aiFeedbackResultApi.confirmAiFeedbackResult(props.callFeedback.aiFeedbackResult);
    emit('refetch');
  });
};
</script>

<style lang="scss" scoped>
.el-tag {
  margin-left: $basespace-200;
  padding: $basespace-50 $basespace-100;
  font-weight: 400;

  &.el-tag--info {
    color: $textcolor-light;
    background-color: $basecolor-disabled;
    border-color: $basecolor-disabled;
  }
}

.reminded {
  background-color: $bgcolor-base;
  padding: $basespace-300 0 $basespace-100 0;
  margin-bottom: $basespace-400;
}
</style>
